// Data access for daily_triggers (Caffeine, Alcohol, Exercise, Sleep, Stress).
//
// Manual entry today; Phase 2 M1 will populate the same rows from wearable
// sources by setting <field>_source to 'healthkit' | 'health_connect' |
// 'hrv_proxy'. The upsert path here always stamps 'manual' because every
// call originates from user input on the Today tab.
import { supabase } from "@/integrations/supabase/client";


export type TriggerSource =
  | "manual"
  | "edited"
  | "healthkit"
  | "health_connect"
  | "hrv_proxy"
  | "fitbit"
  | "oura"
  | "garmin"
  | "whoop";


export type TriggerField =
  | "caffeine_count"
  | "alcohol_count"
  | "exercise_minutes"
  | "sleep_hours"
  | "sleep_quality"
  | "stress_level";

export type DailyTriggerRow = {
  id: string;
  user_id: string;
  log_date: string;
  caffeine_count: number;
  caffeine_source: TriggerSource;
  alcohol_count: number;
  alcohol_source: TriggerSource;
  exercise_minutes: number;
  exercise_source: TriggerSource;
  sleep_hours: number | null;
  sleep_quality: number | null;
  sleep_source: TriggerSource;
  stress_level: number | null;
  stress_source: TriggerSource;
  created_at: string;
  updated_at: string;
};

const FIELD_TO_SOURCE: Record<TriggerField, keyof DailyTriggerRow> = {
  caffeine_count: "caffeine_source",
  alcohol_count: "alcohol_source",
  exercise_minutes: "exercise_source",
  sleep_hours: "sleep_source",
  sleep_quality: "sleep_source",
  stress_level: "stress_source",
};

export async function fetchTriggersForDate(
  userId: string,
  dateKey: string,
): Promise<DailyTriggerRow | null> {
  const { data, error } = await supabase
    .from("daily_triggers")
    .select("*")
    .eq("user_id", userId)
    .eq("log_date", dateKey)
    .maybeSingle();
  if (error) throw error;
  return (data as DailyTriggerRow) ?? null;
}

export async function fetchTriggersBetween(
  userId: string,
  startKey: string,
  endKey: string,
): Promise<DailyTriggerRow[]> {
  const { data, error } = await supabase
    .from("daily_triggers")
    .select("*")
    .eq("user_id", userId)
    .gte("log_date", startKey)
    .lte("log_date", endKey)
    .order("log_date", { ascending: true });
  if (error) throw error;
  return (data || []) as DailyTriggerRow[];
}

// Upsert a single field for `dateKey`. Creates the row with all defaults if
// it doesn't exist, otherwise patches just the requested field plus its
// _source = 'manual'. Race-safe enough for manual entry: each call is one
// PostgREST upsert keyed on the (user_id, log_date) unique index.
export async function upsertTrigger(
  userId: string,
  dateKey: string,
  field: TriggerField,
  value: number | null,
  source: TriggerSource = "manual",
): Promise<void> {
  const sourceCol = FIELD_TO_SOURCE[field];
  const payload: Record<string, any> = {
    user_id: userId,
    log_date: dateKey,
    [field]: value,
    [sourceCol]: source,
    updated_at: new Date().toISOString(),
  };
  const { error } = await supabase
    .from("daily_triggers")
    .upsert(payload as any, { onConflict: "user_id,log_date" });
  if (error) throw error;
}

export async function deleteTriggerForDate(
  userId: string,
  dateKey: string,
): Promise<void> {
  const { error } = await supabase
    .from("daily_triggers")
    .delete()
    .eq("user_id", userId)
    .eq("log_date", dateKey);
  if (error) throw error;
}
